import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, Observable, of, tap } from "rxjs";
import { Category } from "src/app/model/category.model";
import { Monthlypass } from "src/app/model/monthlypack.model";
import { Offer } from "src/app/model/offer.model";
import { Product } from "src/app/model/product.model";
import { ProductZone } from "src/app/model/product-zone.model";
import { IService } from "src/app/model/service.model";
import { IServiceProvider } from "src/app/model/service-provider.model";
import { ITable } from "src/app/model/table.model";
import { Brand, Tag } from "src/app/model/productInfo.model";
import { environment } from "src/environments/environment";
import { IRetailer } from "src/app/model/retailer.model";

@Injectable({
  providedIn: "root",
})
export class ApiServiceListService {
  constructor(private http: HttpClient) { }

  prodHttpOptions = {
    headers: new HttpHeaders({
      "Access-Control-Allow-Origin": "*",
      "Content-Type": "application/json",
    }),
  };

  private cacheData: { zoneData: any };
  private readonly cacheExpiryTime = 5 * 60 * 1000; // 5 minutes


  imageUpload(params: any, body): Observable<any> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.imageUploadAPIHost;
      return this.http.post<any>(url, body, this.prodHttpOptions);
    }
  }
  // Product API List
  getProductById(params: any, id): Observable<Product> {
    if (params.methodType == "GET") {
      const url =
        environment.baseAPIHost + environment.getProductByIdAPIHost + "/" + id;
      return this.http.get<Product>(url, this.prodHttpOptions);
    }
  }
  getProductList(params: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getProductsListAPIHost;
    return this.http.post<ITable>(url, params.body, this.prodHttpOptions);
  }

  getProductFilterList(): Observable<any> {
    const url = environment.baseAPIHost + environment.getProductFilter;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getProductBySubCategory(body): Observable<Product> {
    const url =
      environment.baseAPIHost +
      environment.getProductBySubCategoryAPIHost;
    return this.http.post<Product>(url, body, this.prodHttpOptions);

  }

  getAdminProductBySubCategory(body): Observable<Product> {
    const url =
      environment.baseAPIHost +
      environment.getAdminProductBySubCategoryAPIHost;
    return this.http.post<Product>(url, body, this.prodHttpOptions);

  }
  addProductAPI(params: any, body): Observable<Product> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.addProductAPIHost;
      return this.http.post<Product>(url, body, this.prodHttpOptions);
    }
  }

  updateProduct(params: any, body): Observable<Product> {
    if (params.methodType == "PUT") {
      const url = environment.baseAPIHost + environment.updateProductAPIHost;
      return this.http.put<Product>(url, body, this.prodHttpOptions);
    }
  }

  updateProductZone(params: any, body): Observable<ProductZone> {
    if (params.methodType == "PUT") {
      const url = environment.baseAPIHost + environment.updateProductZoneLink;
      return this.http.put<ProductZone>(url, body, this.prodHttpOptions);
    }
  }


  deleteProductAPI(body): Observable<Monthlypass> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url = environment.baseAPIHost + environment.deleteProductAPIHost;
    return this.http.delete<Monthlypass>(url, deletHttpOptions);
  }


  getRKMRTProducts(): Observable<any> {
    const url = environment.baseAPIHost + environment.getRKMRTProductsAPIHost;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addRKMRTProducts(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addRKMRTProductAPIHost;
    return this.http.post(url, body, this.prodHttpOptions);
  }

  generateRkmrtProductBarcode(body): Observable<any> {
    const url = environment.baseAPIHost + environment.generateRKMRTProductAPIHost;
    return this.http.post(url, body, this.prodHttpOptions);
  }

  getRkmrtProductBYBarcode(barcode): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getRKMRTProductByBarcodeAPIHost + "/" + barcode;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getRkmrtProductBYId(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getRKMRTProductByBarcodeAPIHost + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateRKMRTProduct(body): Observable<any> {
    const url = environment.baseAPIHost + environment.updateRKMRTProductAPIHost;
    return this.http.put(url, body, this.prodHttpOptions);
  }

  getRKMRTProductTableListAPIHost(params: any): Observable<any> {
    const url = environment.baseAPIHost + environment.getRKMRTProductTableListAPIHost;
    return this.http.post<ITable>(url, params.body, this.prodHttpOptions);
  }

  getRKMRTProductFilter(): Observable<any> {
    const url = environment.baseAPIHost + environment.getRKMRTProductFilter;
    return this.http.get<any>(url, this.prodHttpOptions);
  }


  // Category API List
  getAllCategories(params: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getAllCategoryListAPIHost;
    return this.http.post<ITable>(url, params.body, this.prodHttpOptions);
  }
  getTagsList(params?: any): Observable<Tag[]> {
    const url = environment.baseAPIHost + environment.getTagsListAPIHost;
    const type = String(params?.type || "").trim().toLowerCase();
    const requestOptions = type
      ? { ...this.prodHttpOptions, params: new HttpParams().set("type", type) }
      : this.prodHttpOptions;
    return this.http.get<Tag[]>(url, requestOptions);
  }

  getProductsByTag(body) {
    const url = environment.baseAPIHost + environment.getProductsByTagAPI;
    return this.http.post<ITable>(url, body, this.prodHttpOptions);
  }

  getBrandsList(): Observable<Brand[]> {
    const url = environment.baseAPIHost + environment.getBrandsListAPIHost;
    return this.http.get<Brand[]>(url, this.prodHttpOptions);
  }

  getZonesList(forceRefresh = false): Observable<any> {
    const url = environment.baseAPIHost + environment.getZoneListAPI;

    const now = Date.now();
    const cached = this.cacheData?.zoneData;

    if (!forceRefresh && cached && cached.expiresAt > now) {
      return of(cached.data);
    }

    return this.http.get<any>(url, this.prodHttpOptions).pipe(
      tap((data) => {
        if (data.success) {
          this.cacheData = { ...this.cacheData, zoneData: { data: data, expiresAt: now + this.cacheExpiryTime } };
        }
      })
    );
  }

  getZoneListTableAPI(params): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getZoneTableListLink;
    return this.http.post<ITable>(url, params.body, this.prodHttpOptions);
  }

  getZoneFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getZoneFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  deleteZoneAPI(body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url = environment.baseAPIHost + environment.deleteZoneAPI;
    return this.http.delete<any>(url, deletHttpOptions);
  }


  addZoneDetailsAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addZoneAdminAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateZoneAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.updateZoneAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getZoneById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getZoneByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }



  getStatesList(): Observable<any> {
    const url = environment.baseAPIHost + environment.statesListAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addState(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.statesAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateState(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.statesAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getRegionsList(state: string = ""): Observable<any> {
    const url = environment.baseAPIHost + environment.regionsListAPI;
    const requestOptions = state
      ? { ...this.prodHttpOptions, params: new HttpParams().set("state", state) }
      : this.prodHttpOptions;
    return this.http.get<any>(url, requestOptions);
  }

  addRegion(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.regionsAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateRegion(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.regionsAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getDistrictsList(state: string = "", region: string = ""): Observable<any> {
    const url = environment.baseAPIHost + environment.districtsListAPI;
    let params = new HttpParams();
    if (state) {
      params = params.set("state", state);
    }
    if (region) {
      params = params.set("region", region);
    }
    const requestOptions = params.keys().length ? { ...this.prodHttpOptions, params } : this.prodHttpOptions;
    return this.http.get<any>(url, requestOptions);
  }

  addDistrict(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.districtsAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateDistrict(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.districtsAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getCitiesList(state: string = "", region: string = "", district: string = ""): Observable<any> {
    const url = environment.baseAPIHost + environment.citiesListAPI;
    let params = new HttpParams();
    if (state) {
      params = params.set("state", state);
    }
    if (region) {
      params = params.set("region", region);
    }
    if (district) {
      params = params.set("district", district);
    }
    const requestOptions = params.keys().length ? { ...this.prodHttpOptions, params } : this.prodHttpOptions;
    return this.http.get<any>(url, requestOptions);
  }

  addCity(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.citiesAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateCity(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.citiesAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getAreasList(city: string = ""): Observable<any> {
    const url = environment.baseAPIHost + environment.areasListAPI;
    const requestOptions = city
      ? { ...this.prodHttpOptions, params: new HttpParams().set("city", city) }
      : this.prodHttpOptions;
    return this.http.get<any>(url, requestOptions);
  }

  addArea(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.areasAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateArea(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.areasAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }
  saveTags(params: any, body): Observable<Tag[]> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.postTagsListAPIHost;
      return this.http.post<Tag[]>(url, body, this.prodHttpOptions);
    }
  }

  updateTag(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.updateTagAPIHost + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  deleteTag(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.deleteTagAPIHost + "/" + id;
    return this.http.delete<any>(url, this.prodHttpOptions);
  }
  getMainCategoryList(params: any, model?: string): Observable<Category[]> {
    if (params.methodType == "GET") {
      const url =
        environment.baseAPIHost + environment.getMainCategoryListAPIHost;
      const requestOptions = model
        ? { ...this.prodHttpOptions, params: new HttpParams().set("model", model) }
        : this.prodHttpOptions;
      return this.http.get<Category[]>(url, requestOptions);
    }
  }

  getKitchenMainCategory(params: any): Observable<any> {
    if (params.methodType == "GET") {
      const url =
        environment.baseAPIHost + environment.getKitchenMainCategoryAPIHost;
      return this.http.get<any>(url, this.prodHttpOptions);
    }
  }

  getCategoryFilterList(): Observable<any> {
    const url = environment.baseAPIHost + environment.getCategoryFilter;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  deleteCategory(body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url = environment.baseAPIHost + environment.deleteCategoryAPIHost;
    return this.http.delete<any>(url, deletHttpOptions);
  }

  getCategoryList(params: any, body): Observable<Category[]> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.getCategoryListAPIHost;
      return this.http.post<Category[]>(url, body, this.prodHttpOptions);
    }
  }
  getSubCategoryList(params: any, body): Observable<Category[]> {
    if (params.methodType == "POST") {
      const url =
        environment.baseAPIHost + environment.getSubCategoryListAPIHost;
      return this.http.post<Category[]>(url, body, this.prodHttpOptions);
    }
  }
  addCategoryAPI(params: any, body): Observable<Category> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.addCategoryAPIHost;
      return this.http.post<Category>(url, body, this.prodHttpOptions);
    }
  }
  updateCategory(params: any, body) {
    if (params.methodType == "PUT") {
      const url = environment.baseAPIHost + environment.updateCategoryAPIHost;
      return this.http.put<Category>(url, body, this.prodHttpOptions);
    }
  }
  getCategoryById(params: any, id): Observable<Category> {
    if (params.methodType == "GET") {
      const url =
        environment.baseAPIHost + environment.getCategoryById + "/" + id;
      return this.http.get<Category>(url, this.prodHttpOptions);
    }
  }

  // Offers API List
  addOfferAPI(params: any, body): Observable<Offer> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.addOfferAPIHost;
      return this.http.post<Offer>(url, body, this.prodHttpOptions);
    }
  }
  updateOfferAPI(params: any, body): Observable<Offer> {
    if (params.methodType == "PUT") {
      const url = environment.baseAPIHost + environment.editOffersAPIHost;
      return this.http.put<Offer>(url, body, this.prodHttpOptions);
    }
  }
  getOffersAPI(params: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getOffersAPIHost;
    return this.http.post<ITable>(url, params.body, this.prodHttpOptions);
  }

  getOfferFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getOffersFilterAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getOfferByGroupAPI(params: any, id): Observable<Offer> {
    if (params.methodType == "GET") {
      const url =
        environment.baseAPIHost + environment.getOfferByGroupAPIHost + id;
      return this.http.get<Offer>(url, this.prodHttpOptions);
    }
  }
  deleteOfferAPI(body: any): Observable<Offer> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };

    const url = environment.baseAPIHost + environment.deleteOfferAPIHost;
    return this.http.delete<Offer>(url, deletHttpOptions);
  }

  // getOfferSection() {
  //   const url = environment.baseAPIHost + environment.getOfferSectionAPI;
  //   return this.http.get<any>(url, this.prodHttpOptions);
  // }

  getOfferSection(body) {
    const url = environment.baseAPIHost + environment.getOfferSectionAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateOfferSection(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editOfferSectionAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  clearOfferSection(body): Observable<any> {
    const url = environment.baseAPIHost + environment.clearOfferSectionAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getOfferSectionGroup(body: any): Observable<any> {
    const url =
      environment.baseAPIHost + environment.postOfferSectionGroupIdAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // Monthly Pack API List
  addMonthlyPackAPI(params: any, body): Observable<Monthlypass> {
    if (params.methodType == "POST") {
      const url = environment.baseAPIHost + environment.addMonthPackAPIHost;
      return this.http.post<Monthlypass>(url, body, this.prodHttpOptions);
    }
  }

  // Monthly Pack API List
  editMonthlyPackAPI(body): Observable<Monthlypass> {
    const url = environment.baseAPIHost + environment.editMonthlyPackAPI;
    return this.http.put<Monthlypass>(url, body, this.prodHttpOptions);
  }

  getMonthlyPackAPI(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getMonthPackAPIHost;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getMonthlyPackByIdAPI(params: any, id): Observable<Monthlypass> {
    if (params.methodType == "GET") {
      const url =
        environment.baseAPIHost + environment.getMonthlyPackByIdAPIHost + id;
      return this.http.get<Monthlypass>(url, this.prodHttpOptions);
    }
  }
  deleteMonthlyAPI(params: any, body): Observable<Monthlypass> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    if (params.methodType == "DELETE") {
      const url =
        environment.baseAPIHost + environment.deleteMonthlyPackAPIHost;
      return this.http.delete<Monthlypass>(url, deletHttpOptions);
    }
  }
  getMonthlyPackFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getMonthlyPackFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addRetailerDetailsAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addRetailerAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getRetailerFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getRetailerFilterAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getRetailerData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getRetailerAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }


  deleteRetailerAPI(params: any, body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    if (params.methodType == "DELETE") {
      const url =
        environment.baseAPIHost + environment.deleteRetailerAPI;
      return this.http.delete<any>(url, deletHttpOptions);
    }
  }

  getRetailerById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getRetailerById + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateRetailerAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editRetailerAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getRetailerOrdersFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getRetailerOrdersFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getRetailerOrdersTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getRetailerOrdersLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getRetailerByZoneData(body) {
    const url = environment.baseAPIHost + environment.getRetailerByZone;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getRetailersListByZoneAndType(body: { zone: string; type?: string | string[] }) {
    const url = environment.baseAPIHost + environment.getRetailerByZone;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getRetailerOrderIdData(id): Observable<any> {
    const url = environment.baseAPIHost + environment.getRetailerOrderIdLink + "/" + id;;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateRetailerOrderData(body) {
    const url = environment.baseAPIHost + environment.updateRetailerOrderLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getSupplierFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addSupplier(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.addSupplierLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateSupplier(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.editSupplierLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getSupplierAgreementFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierAgreementFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierAgreementTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierAgreementLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierAgreementById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierAgreementByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addSupplierAgreement(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.addSupplierAgreementLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateSupplierAgreement(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.editSupplierAgreementLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getSupplierOrderFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierOrderFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierOrderTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierOrderLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierOrderById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierOrderByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateSupplierOrder(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.updateSupplierOrderLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  generateSupplierInvoice(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.generateSupplierInvoiceLink + "/" + id + "/invoice";
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  getSupplierInvoiceFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierInvoiceFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierInvoiceTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierInvoiceLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierInvoiceById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierInvoiceByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierCreditNoteFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierCreditNoteFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierCreditNoteTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierCreditNoteLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierCreditNoteById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierCreditNoteByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addSupplierCreditNote(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.addSupplierCreditNoteLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getSupplierSettlementFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierSettlementFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierSettlementTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierSettlementLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierSettlementById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierSettlementByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  addSupplierSettlement(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.addSupplierSettlementLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateSupplierSettlement(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.editSupplierSettlementLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }


  getProductPromotionListTableAPI(params): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getProductPromotionTableLink;
    return this.http.post<ITable>(url, params.body, this.prodHttpOptions);
  }

  getProductPromotionFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getProductPromotionFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }


  addProductPromotionDetailsAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addProductPromotionLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateProductPromotionAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.updateProductPromotionLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  deleteProductPromotionAPI(body): Observable<Monthlypass> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url = environment.baseAPIHost + environment.deleteProductPromotionLink;
    return this.http.delete<Monthlypass>(url, deletHttpOptions);
  }

  getProductPromotionById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getProductPromotionLInk + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }


  // Users
  getUsersFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getUsersFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getUsersTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getUsersLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }


  // Deliver Agent

  addDeliveryAgentAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addDeliveryAgentLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getDeliveryAgentFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getDeliveryAgentFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDeliveryAgentTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getDeliveryAgentLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getDeliveryAgentOrderStats(body) {
    const url = environment.baseAPIHost + environment.deliveryAgentOrderSats;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getActiveDeliveryAgents(zoneId) {
    const url = environment.baseAPIHost + environment.getActiveDeliveryAgents;
    return this.http.post<any>(url, { zone: zoneId }, this.prodHttpOptions);
  }

  getDeliveryAgentPayouts(payload) {
    const url = environment.baseAPIHost + environment.deliveryAgentPayouts;
    return this.http.post<any>(url, payload, this.prodHttpOptions);
  }

  deliveryAgentPaymentUpdate(payload) {
    const url = environment.baseAPIHost + environment.deliveryAgentPaymentUpdate;
    return this.http.post<any>(url, payload, this.prodHttpOptions);
  }

  // deleteDeliveryAgentAPI(params: any, body): Observable<any> {
  //   let deletHttpOptions = {
  //     headers: this.prodHttpOptions.headers,
  //     body: body,
  //   };
  //   if (params.methodType == "DELETE") {
  //     const url =
  //       environment.baseAPIHost + environment.deleteDeliveryAgentLink;
  //     return this.http.delete<any>(url, deletHttpOptions);
  //   }
  // }

  getDeliveryAgentById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getDeliveryAgentByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateDeliveryAgentAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editDeliveryAgentLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }



  // User Representative

  addUserRepresentativeAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addUserRepresentativeLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getUserRepresentativeFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getUserRepresentativeFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getUserRepresentativeTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getUserRepresentativeLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getUserRepCodeGenerator(body) {
    const url = environment.baseAPIHost + environment.userRepCodeGenerate;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }
  getUserRepOrderStats(body) {
    const url = environment.baseAPIHost + environment.userRepOrderSats;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getUserRepPayments(body) {
    const url = environment.baseAPIHost + environment.userRepPayouts;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  userRepPaymentUpdate(body) {
    const url = environment.baseAPIHost + environment.userRepPaymentUpdate;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // deleteUserRepresentativeAPI(params: any, body): Observable<any> {
  //   let deletHttpOptions = {
  //     headers: this.prodHttpOptions.headers,
  //     body: body,
  //   };
  //   if (params.methodType == "DELETE") {
  //     const url =
  //       environment.baseAPIHost + environment.deleteUserRepresentativeLink;
  //     return this.http.delete<any>(url, deletHttpOptions);
  //   }
  // }

  getUserRepresentativeById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getUserRepresentativeByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateUserRepresentativeAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editUserRepresentativeLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  // Service Provider
  addServiceProviderAPI(body): Observable<IServiceProvider> {
    const url = environment.baseAPIHost + environment.addServiceProviderLink;
    return this.http.post<IServiceProvider>(url, body, this.prodHttpOptions);
  }

  getServiceProviderFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceProviderFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getServiceProviderTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getServiceProviderLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getServiceProviderById(id): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceProviderByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateServiceProviderAPI(body): Observable<IServiceProvider> {
    const url = environment.baseAPIHost + environment.editServiceProviderLink;
    return this.http.put<IServiceProvider>(url, body, this.prodHttpOptions);
  }

  deleteServiceProviderAPI(body): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body,
    };
    const url = environment.baseAPIHost + environment.deleteServiceProviderLink;
    return this.http.delete<any>(url, deleteHttpOptions);
  }

  getServiceProvidersByCategory(body): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceProvidersByCategoryLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getServiceBookingTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getServiceBookingsLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getServiceBookingById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceBookingByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  assignServiceProviderToBooking(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.assignServiceProviderToBookingLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  // Services
  getServiceCategories(): Observable<any> {
    const url = environment.baseAPIHost + environment.getCategoryByModel;
    return this.http.post<any>(url, { model: "service" }, this.prodHttpOptions);
  }

  getPackageCategories(): Observable<any> {
    const url = environment.baseAPIHost + environment.getCategoryByModel;
    return this.http.post<any>(url, { model: "package" }, this.prodHttpOptions);
  }

  getCategoriesByModel(model: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getCategoryByModel;
    return this.http.post<any>(url, { model }, this.prodHttpOptions);
  }

  // Devotional Library
  getDevotionalLibraryConfig(): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalLibraryConfigAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDevotionalLibraryDashboard(): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalLibraryDashboardAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getComicSeriesFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.comicSeriesFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getComicSeriesTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.comicSeriesListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addComicSeries(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.comicSeriesAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getComicSeriesById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.comicSeriesAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateComicSeries(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.comicSeriesAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishComicSeries(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.comicSeriesAPI + "/" + id + "/publish";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  archiveComicSeries(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.comicSeriesAPI + "/" + id + "/archive";
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  deleteComicSeries(id: string): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
    };
    const url = environment.baseAPIHost + environment.comicSeriesAPI + "/" + id;
    return this.http.delete<any>(url, deleteHttpOptions);
  }

  private comicEpisodeSeriesPath(seriesId: string, suffix = ""): string {
    return environment.comicEpisodeAPI.replace("{seriesId}", seriesId) + suffix;
  }

  private comicEpisodeSeriesFilterPath(seriesId: string): string {
    return environment.comicEpisodeFiltersAPI.replace("{seriesId}", seriesId);
  }

  private comicEpisodeSeriesListPath(seriesId: string): string {
    return environment.comicEpisodeListAPI.replace("{seriesId}", seriesId);
  }

  getComicEpisodeFilterDetails(seriesId: string): Observable<any> {
    const url = environment.baseAPIHost + this.comicEpisodeSeriesFilterPath(seriesId);
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getComicEpisodeTableData(seriesId: string, parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + this.comicEpisodeSeriesListPath(seriesId);
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addComicEpisode(seriesId: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + this.comicEpisodeSeriesPath(seriesId);
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getComicEpisodeById(seriesId: string, episodeId: string): Observable<any> {
    const url = environment.baseAPIHost + this.comicEpisodeSeriesPath(seriesId, "/" + episodeId);
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateComicEpisode(seriesId: string, episodeId: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + this.comicEpisodeSeriesPath(seriesId, "/" + episodeId);
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishComicEpisode(seriesId: string, episodeId: string, body: any): Observable<any> {
    const url =
      environment.baseAPIHost + this.comicEpisodeSeriesPath(seriesId, "/" + episodeId + "/publish");
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  archiveComicEpisode(seriesId: string, episodeId: string): Observable<any> {
    const url =
      environment.baseAPIHost + this.comicEpisodeSeriesPath(seriesId, "/" + episodeId + "/archive");
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  deleteComicEpisode(seriesId: string, episodeId: string): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
    };
    const url = environment.baseAPIHost + this.comicEpisodeSeriesPath(seriesId, "/" + episodeId);
    return this.http.delete<any>(url, deleteHttpOptions);
  }

  getBookFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.bookFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getBookTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.bookListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addBook(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.booksAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getBookById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.booksAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateBook(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.booksAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishBook(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.booksAPI + "/" + id + "/publish";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  archiveBook(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.booksAPI + "/" + id + "/archive";
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  deleteBook(id: string): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
    };
    const url = environment.baseAPIHost + environment.booksAPI + "/" + id;
    return this.http.delete<any>(url, deleteHttpOptions);
  }

  getStotramFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.stotramFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getStotramTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.stotramListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addStotram(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.stotramsAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getStotramById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.stotramsAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateStotram(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.stotramsAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishStotram(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.stotramsAPI + "/" + id + "/publish";
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  archiveStotram(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.stotramsAPI + "/" + id + "/archive";
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  deleteStotram(id: string): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
    };
    const url = environment.baseAPIHost + environment.stotramsAPI + "/" + id;
    return this.http.delete<any>(url, deleteHttpOptions);
  }

  getDeityGroupFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.deityGroupFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDeityGroupTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.deityGroupListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addDeityGroup(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.deityGroupsAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getDeityGroupById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.deityGroupsAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateDeityGroup(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.deityGroupsAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  deleteDeityGroup(id: string): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
    };
    const url = environment.baseAPIHost + environment.deityGroupsAPI + "/" + id;
    return this.http.delete<any>(url, deleteHttpOptions);
  }

  // Devotional Events
  getDevotionalEventsConfig(): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventsConfigAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDevotionalEventsDashboard(): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventsDashboardAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getEventTypeTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.devotionalEventTypesListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addEventType(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventTypesAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getEventTypeById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventTypesAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateEventType(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventTypesAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getEventOrganizerTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.devotionalEventOrganizersListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addEventOrganizer(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventOrganizersAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getEventOrganizerById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventOrganizersAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateEventOrganizerApproval(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventOrganizersAPI + "/" + id + "/approval";
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getTicketValidatorTableData(parameter: any, ownerContext = "admin"): Observable<ITable> {
    const path = ownerContext === "templeadmin"
      ? environment.templeAdminValidatorsListAPI
      : ownerContext === "eventadmin"
        ? environment.eventAdminValidatorsListAPI
        : environment.devotionalEventValidatorsListAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addTicketValidator(body: any, ownerContext = "admin"): Observable<any> {
    const path = ownerContext === "templeadmin"
      ? environment.templeAdminValidatorsAPI
      : ownerContext === "eventadmin"
        ? environment.eventAdminValidatorsAPI
        : environment.devotionalEventValidatorsAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTicketValidatorById(id: string, ownerContext = "admin"): Observable<any> {
    const path = ownerContext === "templeadmin"
      ? environment.templeAdminValidatorsAPI
      : ownerContext === "eventadmin"
        ? environment.eventAdminValidatorsAPI
        : environment.devotionalEventValidatorsAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDevotionalEventTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.devotionalEventsListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getDevotionalEventById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventsAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDevotionalEventBookingTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.devotionalEventBookingsListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getDevotionalEventBookingById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventBookingsAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  refundDevotionalEventBooking(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventBookingsAPI + "/" + id + "/refund";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getDevotionalEventInvoiceTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.devotionalEventInvoicesListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getDevotionalEventInvoiceById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventInvoicesAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  generateDevotionalEventInvoice(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.devotionalEventsAPI + "/" + id + "/invoice";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // Temple Management
  getTempleConfig(): Observable<any> {
    const url = environment.baseAPIHost + environment.templeConfigAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getTempleDashboard(): Observable<any> {
    const url = environment.baseAPIHost + environment.templeDashboardAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getTempleTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.templesListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addTemple(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.templesAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTempleById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.templesAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateTemple(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.templesAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  updateTempleApproval(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.templesAPI + "/" + id + "/approval";
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getTemplePoojaTableData(parameter: any, isTempleAdmin = false): Observable<ITable> {
    const path = isTempleAdmin ? environment.templeAdminPoojasListAPI : environment.templePoojasListAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addTemplePooja(body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminPoojasAPI : environment.templePoojasAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTemplePoojaById(id: string, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminPoojasAPI : environment.templePoojasAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateTemplePooja(id: string, body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminPoojasAPI : environment.templePoojasAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishTemplePooja(id: string, body: any = {}, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminPoojasAPI : environment.templePoojasAPI;
    const url = environment.baseAPIHost + path + "/" + id + "/publish";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  cancelTemplePooja(id: string, body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminPoojasAPI : environment.templePoojasAPI;
    const url = environment.baseAPIHost + path + "/" + id + "/cancel";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTempleEventTableData(parameter: any, isTempleAdmin = false): Observable<ITable> {
    const path = isTempleAdmin ? environment.templeAdminEventsListAPI : environment.templeEventsListAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addTempleEvent(body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminEventsAPI : environment.templeEventsAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTempleEventById(id: string, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminEventsAPI : environment.templeEventsAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateTempleEvent(id: string, body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminEventsAPI : environment.templeEventsAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishTempleEvent(id: string, body: any = {}, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminEventsAPI : environment.templeEventsAPI;
    const url = environment.baseAPIHost + path + "/" + id + "/publish";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  cancelTempleEvent(id: string, body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminEventsAPI : environment.templeEventsAPI;
    const url = environment.baseAPIHost + path + "/" + id + "/cancel";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTempleBookingTableData(parameter: any, isTempleAdmin = false): Observable<ITable> {
    const path = isTempleAdmin ? environment.templeAdminBookingsListAPI : environment.templeBookingsListAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getTempleBookingById(id: string, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminBookingsAPI : environment.templeBookingsAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  createOfflineTemplePoojaBooking(body: any, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminBookingsAPI : environment.templeBookingsAPI;
    const url = environment.baseAPIHost + path + "/offline";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getTempleTicketTableData(parameter: any, isTempleAdmin = false): Observable<ITable> {
    const path = isTempleAdmin ? environment.templeAdminTicketsListAPI : environment.templeTicketsListAPI;
    const url = environment.baseAPIHost + path;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getTempleTicketById(id: string, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminTicketsAPI : environment.templeTicketsAPI;
    const url = environment.baseAPIHost + path + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  validateTempleTicket(id: string, body: any = {}, isTempleAdmin = false): Observable<any> {
    const path = isTempleAdmin ? environment.templeAdminTicketsAPI : environment.templeTicketsAPI;
    const url = environment.baseAPIHost + path + "/" + id + "/validate";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // Temple Admin
  getTempleAdminConfig(): Observable<any> {
    const url = environment.baseAPIHost + environment.templeAdminConfigAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getTempleAdminProfile(): Observable<any> {
    const url = environment.baseAPIHost + environment.templeAdminProfileAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateTempleAdminProfile(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.templeAdminProfileAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  // Event Organizer
  getEventAdminConfig(): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminConfigAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getEventAdminProfile(): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminOrganizerProfileAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateEventAdminProfile(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminOrganizerProfileAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getEventAdminEventTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.eventAdminEventsListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addEventAdminEvent(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminEventsAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getEventAdminEventById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminEventsAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateEventAdminEvent(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminEventsAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  publishEventAdminEvent(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminEventsAPI + "/" + id + "/publish";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  cancelEventAdminEvent(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminEventsAPI + "/" + id + "/cancel";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getEventAdminBookingTableData(parameter: any, eventId = ""): Observable<ITable> {
    const path = environment.eventAdminBookingsListAPI.replace("{eventId}", eventId || "all");
    const url = environment.baseAPIHost + path;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getEventAdminBookingById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminBookingsAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getEventAdminInvoiceTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.eventAdminInvoicesListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getEventAdminInvoiceById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminInvoicesAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  generateEventAdminInvoice(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.eventAdminEventsAPI + "/" + id + "/invoice";
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // Subscriptions
  getSubscriptionConfig(): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionConfigAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSubscriptionDashboard(): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionDashboardAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSubscriptionTierTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.subscriptionTiersListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addSubscriptionTier(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionTiersAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getSubscriptionTierById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionTiersAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateSubscriptionTier(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionTiersAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getSubscriptionPlanTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.subscriptionPlansListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addSubscriptionPlan(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionPlansAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getSubscriptionPlanById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionPlansAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateSubscriptionPlan(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionPlansAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getSubscriptionBenefitRuleTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.subscriptionBenefitRulesListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addSubscriptionBenefitRule(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionBenefitRulesAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getSubscriptionBenefitRuleById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionBenefitRulesAPI + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateSubscriptionBenefitRule(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.subscriptionBenefitRulesAPI + "/" + id;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getUserSubscriptionTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.subscriptionUsersListAPI;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  addServiceAPI(body): Observable<IService> {
    const url = environment.baseAPIHost + environment.addServiceLink;
    return this.http.post<IService>(url, body, this.prodHttpOptions);
  }

  getServiceFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getServiceTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getServiceLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getServiceById(id): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateServiceAPI(body): Observable<IService> {
    const url = environment.baseAPIHost + environment.editServiceLink;
    return this.http.put<IService>(url, body, this.prodHttpOptions);
  }

  deleteServiceAPI(body): Observable<any> {
    const deleteHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body,
    };
    const url = environment.baseAPIHost + environment.deleteServiceLink;
    return this.http.delete<any>(url, deleteHttpOptions);
  }


  // Order Management

  getOrdersFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getOrdersFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getOrdersTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getOrdersLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getOrderById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getOrderByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  reassignOrderAgent(body) {
    const url = environment.baseAPIHost + environment.reAssignOrderAgent;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  attachOrderEvidence(body) {
    const url = environment.baseAPIHost + environment.attachOrderEvidence;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  plCheckStatus(body) {
    const url = environment.baseAPIHost + environment.plCheckStatus;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  regenratePL(body) {
    const url = environment.baseAPIHost + environment.regenratePL;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  refundInitiate(body) {
    const url = environment.baseAPIHost + environment.refundInitiate;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  refundCheckStatus(body) {
    const url = environment.baseAPIHost + environment.refundCheckStatus;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  createReplacement(body) {
    const url = environment.baseAPIHost + environment.createReplacement;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  cancelReplacement(body) {
    const url = environment.baseAPIHost + environment.cancelReplacement;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }



  // Payments Management

  getPaymentsFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getPaymentsFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getPaymentsTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getPaymentsLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getPaymentById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getPaymentByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }


  // Coupon Management

  addCouponAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addCouponLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getCouponFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getCouponFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getCouponTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getCouponLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  deleteCouponAPI(body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url =
      environment.baseAPIHost + environment.deleteCouponLink;
    return this.http.delete<any>(url, deletHttpOptions);
  }

  getCouponById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getCouponByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateCouponAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editCouponLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  // Fee Management
  addFeeData(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addFeeLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getFeeFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getFeeFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getFeeTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getFeeLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  deleteFeeAPI(body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url =
      environment.baseAPIHost + environment.deleteFeeLink;
    return this.http.delete<any>(url, deletHttpOptions);
  }

  getFeeById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getFeeByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateFeeAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editFeeLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }


  // IncentiveRule Management

  addIncentiveRuleData(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addIncentiveRuleLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getIncentiveRuleFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getIncentiveRuleFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getIncentiveRuleTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getIncentiveRuleLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  deleteIncentiveRuleAPI(body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    const url =
      environment.baseAPIHost + environment.deleteIncentiveRuleLink;
    return this.http.delete<any>(url, deletHttpOptions);
  }

  getIncentiveRuleById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getIncentiveRuleByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateIncentiveRuleAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editIncentiveRuleLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  // Telecaller Agent
  addTelecallerAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.addTelecallerLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  editTelecallerAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editTelecallerAPI;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getTelecallerAPI(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getTelecallersAPIHost;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getTelecallerByIdAPI(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getTelecallerByIdAPIHost + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }
  deleteTelecallerAPI(params: any, body): Observable<any> {
    let deletHttpOptions = {
      headers: this.prodHttpOptions.headers,
      body: body,
    };
    if (params.methodType == "DELETE") {
      const url =
        environment.baseAPIHost + environment.deleteTelecallerAPIHost;
      return this.http.delete<any>(url, deletHttpOptions);
    }
  }
  getTelecallerFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getTelecallerFiltersAPI;
    return this.http.get<any>(url, this.prodHttpOptions);
  }


  /**
   * @description Validate User phone number
   * @param {object} body - user phone number
   * @returns {Observable<any>}
   * @memberof ApiServiceListService
   */
  phoneUserValidation(body) {
    const url = environment.baseAPIHost + environment.userPhoneValidation;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  bulkUploadProducts(pageInfo): Observable<any> {
    console.log("pageInfo", pageInfo);
    const limit = pageInfo.body.pageSize;
    const offset = (pageInfo.body.currentPage - 1) * pageInfo.body.pageSize;
    const url = environment.baseAPIHost + environment.bulkUploadProductsAPI + `?limit=${limit}&offset=${offset}`;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getBulkUploadDetailsById(id): Observable<any> {
    const url =
      environment.baseAPIHost + environment.getBulkUploadDetailsByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  bulkUploadXLSXAPI(body) {
    const url = environment.baseAPIHost + environment.bulkUploadXLSX;
    return this.http.post<any>(url, body);
  }


  // Dashboard HTTP Call methos
  getDashboardSummary(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.dashboardSummaryAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDashboardTrends(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.dashboardTrendsAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDashboardOrdersBreakdown(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.dashboardOrdersBreakdownAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDashboardPaymentSummary(query: any): Observable<any> {
    // Note: env key spelled "dashboarPaymentSummaryAPI" in your message—use your exact env key
    const url = environment.baseAPIHost + environment.dashboarPaymentSummaryAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDeliveryPerformance(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.deliveryPerformanceAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDeliveryAlerts(payload): Observable<any> {
    let query = {
      limit: payload.pageSize || 10,
      page: payload.currentPage || 1,
    }
    const url = environment.baseAPIHost + environment.deliveryAlertsAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getPanchangamCalendar(year: number): Observable<any> {
    const url = environment.baseAPIHost + environment.getPanchangamCalendarAPI;
    const params = new HttpParams().set("year", String(year));
    return this.http.get<any>(url, { ...this.prodHttpOptions, params });
  }

  savePanchangamCalendar(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.savePanchangamCalendarAPI;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  fetchDashboardAll(query: any): Observable<{
    summary: any;
    trends: any;
    ordersBreakdown: any;
    paymentSummary: any;
    deliveryPerformance: any;
    // inventoryAlerts: any;
  }> {
    return forkJoin({
      summary: this.getDashboardSummary(query),
      trends: this.getDashboardTrends(query),
      ordersBreakdown: this.getDashboardOrdersBreakdown(query),
      paymentSummary: this.getDashboardPaymentSummary(query),
      deliveryPerformance: this.getDeliveryPerformance(query),
      // inventoryAlerts: this.getDeliveryAlerts(),
    });
  }
  private buildParams(q: Record<string, any>): HttpParams {
    let p = new HttpParams();
    for (const [k, v] of Object.entries(q)) {
      if (v == null || v === '') continue;
      p = p.set(k, v instanceof Date ? v.toISOString() : String(v));
    }
    return p;
  }
}


